import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { anthemEmpirePosSchema } from "~/schemas/SignUp/InsuranceProvider";
import { RadioCardGroup } from "~/shared/components/ui/RadioCardGroup";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { InsuranceProviderFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

// The values are sent to the API and validated against the AnthemEmpirePosAnswer enum,
// so they are the wire format, not display strings.
const ANTHEM_EMPIRE_POS_OPTIONS = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
  { label: "Not sure", value: "Not sure" },
];

/**
 * Asked on the BCBS -> Anthem path only. Empire POS is billed differently than the rest
 * of Anthem and does not show up in the payer data we receive, so the card itself is the
 * only source. A "Yes" or "Not sure" from a New York patient flags the appointment for a
 * manual pre-appointment review.
 */
export const AnthemEmpirePosForm = ({
  onPrev,
  onNext,
  stepId = "primary_insurance",
  isSecondaryProvider = false,
}: InsuranceProviderFormProps & { stepId?: string }) => {
  const { patient, setPatientInsuranceField } = useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.anthemEmpirePos
    : primaryInsurance.anthemEmpirePos;

  const [selectedValue, setSelectedValue] = useState<string>(
    defaultValue ?? "",
  );
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "anthem_empire_pos",
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof anthemEmpirePosSchema>>({
    mode: "onBlur",
    resolver: zodResolver(anthemEmpirePosSchema),
    defaultValues: {
      anthemEmpirePos: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setSelectedValue(defaultValue ?? "");
    setValue("anthemEmpirePos", defaultValue ?? "");
  }, [defaultValue, setValue]);

  const handleSelectionChange = (value: string) => {
    setSelectedValue(value);
    setValue("anthemEmpirePos", value, { shouldValidate: true });
    // On the frontier (first encounter), a selection advances immediately;
    // on a revisited step the user changes freely and clicks Next.
    if (autoAdvanceOnSelect) {
      void handleSubmit(onSubmit, onError)();
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof anthemEmpirePosSchema>> = (
    data,
  ) => {
    setPatientInsuranceField(
      "anthemEmpirePos",
      data.anthemEmpirePos,
      isSecondaryProvider,
    );
    trackComplete({ anthem_empire_pos: data.anthemEmpirePos });

    (onNext as (response?: string) => void)(data.anthemEmpirePos);
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField("anthemEmpirePos", undefined, isSecondaryProvider);
    trackBack();
    onPrev();
  };

  const canProceed = selectedValue !== "";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="w-full max-w-[570px] rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">
          Does your Anthem Blue Cross Blue Shield card include Empire POS on the
          card?
        </h1>
        <RadioCardGroup
          options={ANTHEM_EMPIRE_POS_OPTIONS}
          value={selectedValue}
          onChange={handleSelectionChange}
        />
        {errors.anthemEmpirePos && (
          <ErrorMessage message={errors.anthemEmpirePos.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextDisabled={!canProceed}
        helperText="Select an option to continue"
      />
    </form>
  );
};
